import { Fragment, useState, type ReactNode } from "react";
import { Link, useNavigate } from "react-router";
import { CatalogPicker } from "@core/components/CatalogPicker";
import { ChoiceCard } from "@core/components/ChoiceCard";
import { FullNameField } from "@core/components/FullNameField";
import { BudgetBar, List, ListRow } from "@core/components/List";
import { LongText } from "@core/components/LongText";
import { MessageList, useQuickAction } from "@core/components/MessageList";
import { useMessagePlace } from "@core/messagePlace";
import { Segments } from "@core/components/Segments";
import { Sheet } from "@core/components/Sheet";
import { Stepper } from "@core/components/Stepper";
import { Value, ValueRow } from "@core/components/Value";
import { StartXpPurchases } from "./ExperienceStep";
import { ElaboratedRow } from "./elaboration";
import { WeaponCard } from "./weapons";
import { Workshops } from "./crafting";
import { AddItemSheet, AmmoCounter, ItemEditor } from "./inventory";
import { InventoryTree } from "./placement";
import { LossCard, RecoveryActions } from "./losses";
import { MasteryOfferPicker, NameSheet, SpellSchoolSheet, instanceNaming, type SpellSchoolOption } from "./parts";
import type { LocalizedText, MessageView, TermLanguage } from "@core/api/types";
import { alreadyTaken, page, resourceLabel, useSm, type AppearanceRoll, type EntryDetails, type InventoryView, type ShieldView, type SpellNote } from "./view";

/** A collapsed description from a text package, or nothing without one; a label names the entry where several stand together. */
function describe(value: LocalizedText | null | undefined, language: TermLanguage, label?: string) {
  return value ? <LongText value={value} language={language} label={label} /> : undefined;
}

export function OverviewTab() {
  const { view, character, apply, choose, language } = useSm();
  const longText = (value: LocalizedText | null | undefined) => describe(value, language);
  const navigate = useNavigate();
  const messages = character.evaluation.messages;
  const runAction = useQuickAction();
  const placeOf = useMessagePlace();
  const elsewhere = (m: MessageView) => {
    const place = placeOf(m);
    return !!place && place !== "bogen/uebersicht";
  };
  return (
    <div className="sheet-grid">
      {character.fullName && <h2 className="wide sheet-heading">{character.fullName}</h2>}
      <section className="card">
        <h3>Attribute</h3>
        <table className="compact-table">
          <thead>
            <tr>
              <th>Attribut</th>
              <th>Start</th>
              <th>Wert</th>
            </tr>
          </thead>
          <tbody>
            {view.attributes.map((a) => (
              <tr key={a.id}>
                <td>
                  {a.name} <span className="muted">{a.short}</span>
                  {longText(a.description)}
                </td>
                <td>
                  <Value valueKey={a.start} />
                </td>
                <td>
                  <Value valueKey={a.value} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
      <section className="card">
        <h3>Abgeleitete Werte</h3>
        <ValueRow valueKey="derived.gk" label="Größenklasse (GK)" />
        {view.derived
          .filter((d) => d.id !== "gk")
          .map((d) => (
            d.description ? (
              <div key={d.id}>
                <ValueRow valueKey={d.value} label={`${d.name} (${d.short})`} hint={d.formula} />
                {longText(d.description)}
              </div>
            ) : (
              <ValueRow key={d.id} valueKey={d.value} label={`${d.name} (${d.short})`} hint={d.formula} />
            )
          ))}
        <ValueRow valueKey="splitterpunkte" label="Splitterpunkte" />
        <ValueRow valueKey="behinderung" label="Behinderung" />
        <ValueRow valueKey="tickzuschlag" label="Tick-Zuschlag" />
        <ValueRow valueKey="schadensreduktion" label="Schadensreduktion" />
      </section>
      <section className="card">
        <h3>Herkunft</h3>
        <dl className="facts">
          <dt>Rasse</dt>
          <dd>
            {view.race?.name ?? "–"}
            {longText(view.race?.description)}
          </dd>
          <dt>Kultur</dt>
          <dd>
            {view.culture?.name ?? "–"}
            {longText(view.culture?.description)}
          </dd>
          <dt>Abstammung</dt>
          <dd>
            {view.ancestry?.name ?? "–"}
            {longText(view.ancestry?.description)}
          </dd>
          <dt>Ausbildung</dt>
          <dd>
            {view.education?.name ?? "–"}
            {view.education?.variant && ` (${view.education.variant.name})`}
            {longText(view.education?.description)}
          </dd>
          <dt>Mondzeichen</dt>
          <dd>
            {view.moonSign?.name ?? "–"}
            {longText(view.moonSign?.description)}
            {view.moonSign && view.moonSign.gifts.length > 0 && (
              <ul className="gift-list" aria-label="Gaben des Mondzeichens">
                {view.moonSign.gifts.map((gift) => (
                  <li key={gift.heroLevel} className={gift.unlocked ? undefined : "locked"}>
                    <span className="tag">HG {gift.heroLevel}</span> {gift.summary}
                    {!gift.unlocked && <span className="muted"> (ab Heldengrad {gift.heroLevel})</span>}
                  </li>
                ))}
              </ul>
            )}
          </dd>
          <dt>Sprachen</dt>
          <dd>
            {view.languages.map((l) => l.name).join(", ") || "–"}
            {view.languages
              .filter((l) => l.description)
              .map((l) => (
                <div key={l.id}>
                  <span className="muted small">{l.name}</span>
                  {longText(l.description)}
                </div>
              ))}
          </dd>
          <dt>Heldengrad</dt>
          <dd>
            <Value valueKey="hg" />
          </dd>
        </dl>
      </section>
      <section className="card">
        <h3>Stärken</h3>
        <List>
          {view.strengths.map((s) => (
            <ListRow
              key={`${s.id}${s.param ?? ""}`}
              title={
                <>
                  {s.name}
                  {s.param && <span className="muted"> ({s.param})</span>}
                  {s.count > 1 && <span className="muted"> ×{s.count}</span>}
                </>
              }
              subtitle={
                <>
                  {s.summary && <>{s.summary} · </>}
                  {[...new Set(s.origins)].join(", ")} · {page(s.source)}
                </>
              }
              below={longText(s.description)}
            />
          ))}
        </List>
      </section>
      <section className="card">
        <h3>Ressourcen</h3>
        <List aria-label="Ressourcen">
          {view.resources.length === 0 && <li className="list-empty">Keine.</li>}
          {view.resources.map((r) => {
            const label = r.param ? `${r.name} (${r.param})` : r.name;
            return (
              <ElaboratedRow
                key={r.key}
                className="resource-row"
                title={label}
                badges={
                  r.place ? (
                    <Link className="badge" to={`/c/${character.id}/bogen/ausruestung`} data-place-of={r.key} aria-label={`${label} ist als Ort eingerichtet; zur Ausrüstung`}>
                      als Ort eingerichtet
                    </Link>
                  ) : undefined
                }
                value={<Value valueKey={r.value} label={label} />}
                actions={
                  r.placeable && !r.place ? (
                    // An instance of Zuflucht becomes a place where things and pack animals lie (O5, GRW p. 79–80). The button gives way to
                    // the badge, which takes the focus.
                    <button
                      type="button"
                      className="button secondary small"
                      aria-label={`${label} als Ort einrichten`}
                      onClick={async () => {
                        if (await choose({ type: "sm.addPlace", payload: { ref: r.key } }, "Als Ort eingerichtet")) {
                          window.setTimeout(() => document.querySelector<HTMLElement>(`[data-place-of="${CSS.escape(r.key)}"]`)?.focus(), 0);
                        }
                      }}
                    >
                      Als Ort einrichten
                    </button>
                  ) : undefined
                }
                below={longText(r.description)}
                subject={label}
                elaboration={r}
                onSave={({ note, details }) => apply({ type: "sm.setResourceNote", payload: { key: r.key, text: note, details } })}
              />
            );
          })}
        </List>
        <Workshops />
      </section>
      <section className="card">
        <h3>Schwächen</h3>
        <List>
          {view.weaknesses.length === 0 && <li className="list-empty">Keine.</li>}
          {view.weaknesses.map((w, i) => (
            <ElaboratedRow
              key={i}
              title={w.name}
              below={longText(w.description)}
              subject={w.name}
              elaboration={w}
              onSave={({ note, details }) => apply({ type: "sm.updateWeakness", payload: { index: i, note, details } })}
            />
          ))}
        </List>
      </section>
      {messages.length > 0 && (
        <section className="card wide">
          <h3>Hinweise</h3>
          <MessageList
            messages={messages}
            onAcknowledge={(m) => void apply({ type: "core.acknowledge", payload: { key: m.key } })}
            onUnacknowledge={(m) => void apply({ type: "core.unacknowledge", payload: { key: m.key } })}
            onAction={runAction}
            canNavigate={elsewhere}
            onNavigate={(m) => elsewhere(m) && void navigate(`/c/${character.id}/${placeOf(m)}`)}
          />
        </section>
      )}
    </div>
  );
}

const categories = [
  { id: "allgemein", label: "Allgemeine Fertigkeiten" },
  { id: "kampf", label: "Kampffertigkeiten" },
  { id: "magie", label: "Magieschulen" },
] as const;

export function SkillsTab() {
  const { view, value, language } = useSm();
  const [onlyLearned, setOnlyLearned] = useState(false);
  return (
    <div className="stack">
      <label className="option">
        <input type="checkbox" checked={onlyLearned} onChange={(e) => setOnlyLearned(e.target.checked)} />
        <span>Nur Fertigkeiten mit Punkten</span>
      </label>
      {categories.map((category) => (
        <section key={category.id} className="card">
          <h3>{category.label}</h3>
          {view.skillGroups
            .filter((g) => g.category === category.id)
            .map((g) => (
              <Fragment key={g.id}>{describe(g.description, language, g.name === category.label ? undefined : g.name)}</Fragment>
            ))}
          <table className="compact-table skills-table">
            <thead>
              <tr>
                <th>Fertigkeit</th>
                <th>Attribute</th>
                <th>Punkte</th>
                <th>Wert</th>
              </tr>
            </thead>
            <tbody>
              {view.skills
                .filter((s) => s.category === category.id && (!onlyLearned || (value(s.points)?.number ?? 0) > 0))
                .map((s) => (
                  <tr key={s.id}>
                    <td>
                      {s.name}
                      {describe(s.description, language)}
                      {s.masteries.length > 0 && (
                        <ul className="mastery-list">
                          {s.masteries.map((m, i) => (
                            <li key={`${m.id}${i}`} title={`${m.origin}${m.summary ? ` – ${m.summary}` : ""}`}>
                              {m.name}
                              {m.param && ` (${m.param})`} <span className="source">{page(m.source)}</span>
                              {m.missing && <span className="muted"> (erst nutzbar nach Erwerb von {m.missing})</span>}
                              {describe(m.description, language)}
                            </li>
                          ))}
                        </ul>
                      )}
                    </td>
                    <td className="muted">{s.attributes?.join("/") ?? "je Waffe"}</td>
                    <td>
                      <Value valueKey={s.points} />
                    </td>
                    <td>{s.category === "kampf" ? "–" : <Value valueKey={s.value} />}</td>
                  </tr>
                ))}
            </tbody>
          </table>
        </section>
      ))}
    </div>
  );
}

/** A magic school as a box of its own with a marked head (name and FW), alike on the play page and in the tab „Magie“. */
function SchoolBox(props: { name: string; heading: "h3" | "h4"; value: ReactNode; className?: string; children: ReactNode }) {
  const Heading = props.heading;
  return (
    <section className={`school-box${props.className ? ` ${props.className}` : ""}`} aria-label={props.name}>
      <div className="school-head">
        <Heading>{props.name}</Heading>
        {props.value}
      </div>
      <div className="school-body">{props.children}</div>
    </section>
  );
}

export function MagicTab() {
  const { view, value, language } = useSm();
  const schools = view.skills.filter((s) => s.category === "magie" && (value(s.points)?.number ?? 0) > 0);
  return (
    <div className="stack">
      <section className="card">
        <h3>Fokus</h3>
        <ValueRow valueKey="derived.fo" label="Fokus" />
      </section>
      {schools.length === 0 && <p className="muted">Keine Magieschule mit Punkten.</p>}
      {schools.map((school) => (
        <SchoolBox key={school.id} name={school.name} heading="h3" value={<Value valueKey={school.value} label="FW" showLabel />} className="card">
          <div className="table-scroll">
            <table className="compact-table">
              <thead>
                <tr>
                  <th>Zauber</th>
                  <th>Grad</th>
                  <th>Schw.</th>
                  <th>Kosten</th>
                  <th>Dauer</th>
                  <th>Reichw.</th>
                  <th>Wirkung</th>
                  <th>Bereich</th>
                </tr>
              </thead>
              <tbody>
                {view.spells
                  .filter((spell) => spell.school === school.id)
                  .map((spell) => (
                    <tr key={spell.id}>
                      <td title={spell.summary ?? ""}>
                        {spell.name} <span className="source">{page(spell.source)}</span>
                        {describe(spell.description, language)}
                      </td>
                      <td>{spell.grade}</td>
                      <td>{spell.difficulty}</td>
                      <td>
                        {spell.cost}
                        <SpellNotes notes={spell.costNotes} />
                      </td>
                      <td>{spell.castingTime}</td>
                      <td>
                        {spell.range}
                        <SpellNotes notes={spell.rangeNotes} />
                      </td>
                      <td>{spell.duration}</td>
                      <td>{spell.area ?? ""}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </SchoolBox>
      ))}
    </div>
  );
}

export function EquipmentTab() {
  const { view, character, budget, language } = useSm();
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<InventoryView | null>(null);
  const creation = character.mode === "creation";
  const kit = view.startKit;
  return (
    <div className="stack">
      <WeaponCard />

      {view.shields.length > 0 && (
        <section className="card">
          <h3>Schilde</h3>
          <div className="table-scroll">
            <table className="compact-table" aria-label="Schilde">
              <thead>
                <tr>
                  <th>Schild</th>
                  <th>Abwehr</th>
                  <th>Schildstoß</th>
                  <th>Schaden</th>
                  <th>WGS</th>
                  <th>Merkmale</th>
                </tr>
              </thead>
              <tbody>
                {view.shields.map((s) => (
                  <tr key={s.instance}>
                    <td>
                      {s.name}
                      {!s.equipped && <span className="muted"> (nicht angelegt)</span>}
                      <div className="muted small">{shieldSkills(s)}</div>
                    </td>
                    <td>{s.defense && <Value valueKey={s.defense} />}</td>
                    <td>{s.bash && <Value valueKey={s.bash} />}</td>
                    <td>{s.bashDamage && <Value valueKey={s.bashDamage} />}</td>
                    <td>{s.bashWgs && <Value valueKey={s.bashWgs} />}</td>
                    <td className="small">
                      {s.features.map((f) => f.name).join(", ")}
                      {s.features.map((f, j) => (
                        <Fragment key={j}>{describe(f.description, language, f.name)}</Fragment>
                      ))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      <section className="card stack">
        {creation ? (
          <>
            <div className="row spread">
              <h3>Budgets der Erschaffung</h3>
              <Link to={`/c/${character.id}/assistent/ausruestung`} className="link small">
                Zum Schritt Ausrüstung
              </Link>
            </div>
            <BudgetBar label="Handwerkszeug" used={budget(view.budgets.tools)} total={kit.toolsMax} usedText={<Value valueKey={view.budgets.tools} />} totalText={kit.toolsMaxText} />
            <BudgetBar
              label="Startvermögen"
              used={budget(view.budgets.moneySpent)}
              total={budget(view.budgets.startMoney)}
              usedText={<Value valueKey={view.budgets.moneySpent} />}
              totalText={<Value valueKey={view.budgets.startMoney} />}
              hint={
                <>
                  Übrig: <Value valueKey={view.budgets.money} /> · Startwaffe: <Value valueKey={view.budgets.startWeapon} /> (Richtwert {kit.weaponMaxText})
                </>
              }
            />
          </>
        ) : (
          <ValueRow valueKey={view.budgets.money} label="Geld" />
        )}
      </section>

      <section className="card stack">
        <div className="row spread">
          <h3>Inventar</h3>
          <span className="muted small">
            Gesamtwert <Value valueKey={view.inventoryTotals.value} /> · Last <Value valueKey={view.inventoryTotals.load} />
            {view.carrying && (
              <>
                {" "}
                · Traglast <Value valueKey={view.carrying.carried} /> von <Value valueKey={view.carrying.capacity} />
              </>
            )}
          </span>
        </div>
        <InventoryTree onEdit={setEditing} />
        <div className="actions">
          <button type="button" className="button primary" onClick={() => setAdding(true)}>
            + Gegenstand hinzufügen
          </button>
        </div>
      </section>
      {adding && <AddItemSheet title="Gegenstand hinzufügen" onClose={() => setAdding(false)} />}
      {editing && <ItemEditor item={editing} onClose={() => setEditing(null)} />}
    </div>
  );
}

const descriptionFields = [
  ["gender", "Geschlecht"],
  ["hair", "Haarfarbe"],
  ["eyes", "Augenfarbe"],
  ["skin", "Haut-/Fellfarbe"],
  ["height", "Körpergröße (cm)"],
  ["weight", "Gewicht (kg)"],
  ["birthplace", "Geburtsort"],
  ["deity", "Gottheit"],
] as const;

export function DescriptionTab() {
  const { view, apply, choose } = useSm();
  const [pickingDeity, setPickingDeity] = useState(false);
  const rolls = view.appearance?.rolls ?? {};
  const weight = view.appearance?.weight;
  // The browser draws the dice, so a preview of the same operation gives the same result; the engine looks the sum up and names it in
  // the notice, which also offers to undo the roll.
  const roll = (field: string, label: string, dice: AppearanceRoll) =>
    void choose(
      { type: "sm.rollAppearance", payload: { field, dice: Array.from({ length: dice.count }, () => 1 + Math.floor(Math.random() * dice.sides)) } },
      `${label} gewürfelt`,
    );
  return (
    <div className="stack">
      <section className="card description-grid">
        {descriptionFields.map(([key, label]) => {
          const dice = key in rolls ? rolls[key as keyof typeof rolls] : undefined;
          const hint = dice ? `${dice.formula}${key === "height" ? " cm" : ""}` : key === "weight" && weight ? `${weight.min} bis ${weight.max} kg` : null;
          return (
            <div key={key} className="field">
              <label htmlFor={`sm-description-${key}`} className="muted small">
                {label}
              </label>
              <div className="description-roll">
                <input
                  id={`sm-description-${key}`}
                  // A rolled value replaces what the field shows.
                  key={String(view.description[key] ?? "")}
                  defaultValue={String(view.description[key] ?? "")}
                  inputMode={key === "height" || key === "weight" ? "numeric" : "text"}
                  onBlur={(e) => {
                    const value = key === "height" || key === "weight" ? (e.target.value ? Number(e.target.value) : null) : e.target.value || null;
                    if (value !== (view.description[key] ?? null)) void apply({ type: "sm.setDescription", payload: { [key]: value } });
                  }}
                />
                {dice && (
                  <button type="button" className="button secondary small" aria-label={`${label} würfeln (${dice.formula})`} onClick={() => roll(key, label, dice)}>
                    Würfeln
                  </button>
                )}
                {key === "deity" && (
                  <button type="button" className="button secondary small" aria-label="Gottheit aus der Liste wählen" onClick={() => setPickingDeity(true)}>
                    Liste
                  </button>
                )}
              </div>
              {key === "deity" && view.deity && (
                <span className="muted small">
                  {[view.deity.aspects.join(", "), view.deity.region, view.deity.schools].filter(Boolean).join(" · ")} ({page(view.deity.source)})
                </span>
              )}
              {hint && (
                <span className="muted small">
                  {hint}
                  {view.appearance && ` (${page(view.appearance.source)})`}
                </span>
              )}
            </div>
          );
        })}
      </section>
      {pickingDeity && (
        <CatalogPicker
          type="deities"
          title="Gottheit"
          hint="Die Magieschulen sind Empfehlungen für Priester (+ empfohlen, – unüblich). Eine eigene Gottheit trägst du direkt ins Feld ein."
          onClose={() => setPickingDeity(false)}
          onPick={(item) => {
            setPickingDeity(false);
            if (item.label.de !== view.description.deity) void apply({ type: "sm.setDescription", payload: { deity: item.label.de } });
          }}
        />
      )}
      <section className="card stack">
        <FullNameField />
        <label className="field">
          <span>Aussehen</span>
          <textarea rows={3} defaultValue={String(view.description.appearance ?? "")} onBlur={(e) => void apply({ type: "sm.setDescription", payload: { appearance: e.target.value || null } })} />
        </label>
        <label className="field">
          <span>Hintergrund</span>
          <textarea rows={5} defaultValue={String(view.description.background ?? "")} onBlur={(e) => void apply({ type: "sm.setDescription", payload: { background: e.target.value || null } })} />
        </label>
        <label className="field">
          <span>Notizen</span>
          <textarea rows={5} defaultValue={view.notes} onBlur={(e) => e.target.value !== view.notes && void apply({ type: "sm.setNotes", payload: { text: e.target.value } })} />
        </label>
      </section>
    </div>
  );
}

export function AdvanceTab() {
  const { view, confirm, character, budget } = useSm();
  const [picker, setPicker] = useState<"spells" | "strengths" | "languages" | "resources" | null>(null);
  const [lowering, setLowering] = useState<string | null>(null);
  const [spendingStartXp, setSpendingStartXp] = useState(false);
  const reopened = view.budgets.startXpReopened;
  const [learningMastery, setLearningMastery] = useState(false);
  const [naming, setNaming] = useState<{ id: string; label: string; parameter?: string | null } | null>(null);
  const [schooling, setSchooling] = useState<{ id: string; label: string; schools: SpellSchoolOption[] } | null>(null);
  const careerChoices = character.evaluation.openChoices.filter((c) => !c.step);
  const xp = character.balances.find((b) => b.currency === "ep")?.balance ?? 0;
  const [byGameMaster, setByGameMaster] = useState(false);
  /** Attributes, skills and resources the game master may raise without experience (E28). */
  const raise = byGameMaster ? "sm.advanceByGameMaster" : "sm.advance";
  const raiseLabel = (name: string) => (byGameMaster ? `${name} durch die Spielleitung steigern` : `${name} steigern`);
  const raiseCost = (valueKey: string) => (byGameMaster ? "ohne EP (Spielleitung)" : <RaiseCost valueKey={valueKey} />);
  return (
    <div className="stack">
      <label className="option">
        <input type="checkbox" checked={byGameMaster} onChange={(e) => setByGameMaster(e.target.checked)} />
        <span>Steigerung durch die Spielleitung (ohne EP, für Attribute, Fertigkeiten und Ressourcen)</span>
      </label>
      <section className="card row spread">
        <span>
          Freie EP: <strong>{xp}</strong>
        </span>
        <span>
          Heldengrad <Value valueKey="hg" />
        </span>
      </section>
      {reopened && budget(reopened) > 0 && (
        <section className="card row spread" aria-label="Start-Erfahrung">
          <span>
            Start-Erfahrung wieder offen: <strong><Value valueKey={reopened} /> EP</strong>
            <span className="muted small"> · zu den Kosten der Erschaffung (GRW S. 25)</span>
          </span>
          <button type="button" className="button secondary" onClick={() => setSpendingStartXp(true)}>
            Start-Erfahrung ausgeben
          </button>
        </section>
      )}
      {spendingStartXp && (
        <Sheet title="Start-Erfahrung ausgeben" wide onClose={() => setSpendingStartXp(false)}>
          <StartXpPurchases career />
        </Sheet>
      )}
      <section className="card">
        <h3>Attribute</h3>
        <List>
          {view.attributes.map((a) => (
            <ListRow
              key={a.id}
              title={a.name}
              subtitle={a.cost && raiseCost(a.cost)}
              value={<Value valueKey={a.value} />}
              actions={
                <button type="button" className="button secondary" onClick={() => confirm({ type: raise, payload: { kind: "attribute", target: a.id } }, raiseLabel(a.name))}>
                  +1
                </button>
              }
            />
          ))}
        </List>
      </section>
      <section className="card">
        <h3>Fertigkeiten</h3>
        <List>
          {view.skills.map((s) => (
            <ListRow
              key={s.id}
              title={s.name}
              subtitle={
                <>
                  höchstens {s.max}
                  {s.cost && (
                    <>
                      {" · "}
                      {raiseCost(s.cost)}
                    </>
                  )}
                </>
              }
              value={<Value valueKey={s.points} />}
              actions={
                <button type="button" className="button secondary" onClick={() => confirm({ type: raise, payload: { kind: "skill", target: s.id } }, raiseLabel(s.name))}>
                  +1
                </button>
              }
            />
          ))}
        </List>
      </section>
      <section className="card">
        <h3>Ressourcen</h3>
        <List>
          {view.resources.map((r) => (
            <ListRow
              key={r.key}
              title={r.param ? `${r.name} (${r.param})` : r.name}
              subtitle={
                <>
                  {r.cost && raiseCost(r.cost)}
                  {r.advanceNote && (
                    <div className="muted small">
                      {r.advanceNote.text} <span className="source">{page(r.advanceNote.source)}</span>
                    </div>
                  )}
                </>
              }
              value={<Value valueKey={r.value} />}
              actions={
                <>
                  {(r.lossMax ?? 0) > 0 && (
                    <button type="button" className="button secondary" aria-label={`${resourceLabel(view, r.key)} senken`} onClick={() => setLowering(r.key)}>
                      −
                    </button>
                  )}
                  <button type="button" className="button secondary" onClick={() => confirm({ type: raise, payload: { kind: "resource", target: r.key } }, raiseLabel(r.name))}>
                    +1
                  </button>
                </>
              }
            />
          ))}
        </List>
        <button type="button" className="button secondary" onClick={() => setPicker("resources")}>
          Ressource erwerben
        </button>
      </section>
      {lowering && <LowerResourceSheet resource={lowering} onClose={() => setLowering(null)} />}
      {careerChoices.map((choice) => (
        <ChoiceCard key={choice.id} choice={choice} />
      ))}
      <section className="card">
        <h3>Meisterschaften</h3>
        <MasteryCosts />
        <button type="button" className="button secondary" onClick={() => setLearningMastery(true)}>
          Meisterschaft lernen
        </button>
      </section>
      {learningMastery && <MasteryPurchase onClose={() => setLearningMastery(false)} />}
      <section className="card actions">
        <button type="button" className="button secondary" onClick={() => setPicker("spells")}>
          Zauber lernen
        </button>
        <button type="button" className="button secondary" onClick={() => setPicker("strengths")}>
          Stärke kaufen
        </button>
        <button type="button" className="button secondary" onClick={() => setPicker("languages")}>
          Sprache lernen
        </button>
      </section>
      {picker && (
        <CatalogPicker
          type={picker}
          title={{ spells: "Zauber", strengths: "Stärke", languages: "Sprache", resources: "Ressource" }[picker]}
          unavailable={alreadyTaken(view, picker)}
          onClose={() => setPicker(null)}
          onPick={(item) => {
            const kind = { spells: "spell", strengths: "strength", languages: "language", resources: "resource" }[picker];
            setPicker(null);
            const details = (item.details ?? {}) as { schools?: SpellSchoolOption[] } & EntryDetails;
            if (kind === "resource" && (details.multipleInstances || details.parameter)) {
              setNaming({ id: item.id, label: item.label.de, parameter: details.parameter?.label });
              return;
            }
            if (kind === "spell" && (details.schools?.length ?? 0) > 1) {
              setSchooling({ id: item.id, label: item.label.de, schools: details.schools! });
              return;
            }
            confirm({ type: kind === "resource" ? raise : "sm.advance", payload: { kind, target: item.id, school: details.schools?.[0]?.school } }, kind === "resource" ? raiseLabel(item.label.de) : item.label.de);
          }}
        />
      )}
      {schooling && (
        <SpellSchoolSheet
          spell={schooling.label}
          schools={schooling.schools}
          onClose={() => setSchooling(null)}
          onPick={(option) => {
            setSchooling(null);
            confirm({ type: "sm.advance", payload: { kind: "spell", target: schooling.id, school: option.school } }, schooling.label);
          }}
        />
      )}
      {naming && (
        <NameSheet
          {...instanceNaming(naming.label, naming.parameter)}
          onClose={() => setNaming(null)}
          onSubmit={(name) => {
            setNaming(null);
            confirm({ type: raise, payload: { kind: "resource", target: naming.id, param: name } }, raiseLabel(naming.label));
          }}
        />
      )}
    </div>
  );
}

const lossReasons = ["verkauft", "geopfert", "verloren"];

/**
 * Lowers a resource whose points were lost in play, e.g. a relic sold or sacrificed or a follower who died (GRW p. 75). Nothing is
 * refunded; the preview shows the change before it is saved. How far it goes comes from the view: a core resource down to its
 * minimum, which the preview reports for confirmation below 0, an optional one to 0 (SM-RA-020).
 */
function LowerResourceSheet({ resource, onClose }: { resource: string; onClose: () => void }) {
  const { view, confirm } = useSm();
  const max = view.resources.find((r) => r.key === resource)?.lossMax ?? 0;
  const [amount, setAmount] = useState(1);
  const [reason, setReason] = useState("");
  const label = resourceLabel(view, resource);
  const lower = () => {
    confirm({ type: "sm.lowerResource", payload: { target: resource, amount, reason: reason.trim() || null } }, `${label} senken`);
    onClose();
  };
  return (
    <Sheet
      title={`${label} senken`}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="button secondary" onClick={onClose}>
            Abbrechen
          </button>
          <button type="button" className="button primary" onClick={lower}>
            Senken
          </button>
        </>
      }
    >
      <div className="stack small-gap">
        <div className="row spread">
          <span>Punkte</span>
          <Stepper label="Punkte" value={amount} min={1} max={max} onChange={setAmount} />
        </div>
        <Segments
          ariaLabel="Grund"
          options={lossReasons.map((r) => ({ value: r, label: r[0]!.toUpperCase() + r.slice(1) }))}
          value={reason}
          onChange={(r) => setReason(reason === r ? "" : r)}
        />
        <label className="field">
          <span>Grund</span>
          <input value={reason} placeholder="z. B. Gefolgsmann im Kampf gefallen" onChange={(e) => setReason(e.target.value)} />
        </label>
        <p className="muted small">Punkte einer Ressource können im Spiel verloren gehen; es wird nichts erstattet (GRW S. 75).</p>
      </div>
    </Sheet>
  );
}

/** The EP of a mastery per threshold, as the engine computes them. */
function MasteryCosts() {
  const { view } = useSm();
  if (!view.masteryCosts?.length) return null;
  return (
    <p className="muted small">
      Kosten je Schwelle:{" "}
      {view.masteryCosts.map((key, index) => (
        <span key={key}>
          {index > 0 && " · "}
          {index + 1}: <Value valueKey={key} />
        </span>
      ))}
    </p>
  );
}

/** Learning a mastery: first one of the skills whose points allow a mastery, then one of its masteries (GRW p. 92). */
function MasteryPurchase(props: { onClose: () => void }) {
  const { view, confirm } = useSm();
  return (
    <MasteryOfferPicker
      title="Meisterschaft lernen"
      offers={view.masteryOffers}
      intro="Für welche Fertigkeit? Zur Wahl stehen die Fertigkeiten, deren Punkte für eine Meisterschaft reichen (GRW S. 92)."
      empty="Noch keine Fertigkeit hat genug Punkte für eine Meisterschaft."
      hint={<MasteryCosts />}
      onClose={props.onClose}
      onPick={(mastery, skill) => confirm({ type: "sm.advance", payload: { kind: "mastery", target: mastery.id, skill: skill.id } }, `${mastery.name} in ${skill.name}`)}
    />
  );
}

/** What one more point costs, as the engine computes it; tapping shows the cost table. */
function RaiseCost(props: { valueKey: string }) {
  return (
    <>
      +1 kostet <Value valueKey={props.valueKey} />
    </>
  );
}

/** Label and text of a spell fact, left out when the spell has none. */
function Fact(props: { label: string; children: string; notes?: SpellNote[] }) {
  if (!props.children) return null;
  return (
    <span>
      <span className="muted">{props.label}</span> {props.children}
      <SpellNotes notes={props.notes} />
    </span>
  );
}

/** What masteries of the school change at cost or range, as a hint; the printed value stays (E4, SM-F-078). */
function SpellNotes(props: { notes?: SpellNote[] }) {
  if (!props.notes?.length) return null;
  return (
    <span className="muted small spell-notes">
      {props.notes.map((n) => (
        <span key={n.text}>
          {" "}
          ({n.text}, {page(n.source)})
        </span>
      ))}
    </span>
  );
}

/** Skill and attributes of active defense and bash with a shield, e.g. "Abwehr: Klingenwaffen · INT/STÄ". */
function shieldSkills(shield: ShieldView) {
  return [
    shield.defenseSkill && `Abwehr: ${shield.defenseSkill} · ${shield.defenseAttributes}`,
    shield.bashSkill && `Schildstoß: ${shield.bashSkill} · ${shield.bashAttributes}`,
  ]
    .filter(Boolean)
    .join("; ");
}

/** A value during play, marked when it differs from its base value, e.g. because of the wound penalty. */
export function PlayValue(props: { base: string; play?: string | null; label: string; showLabel?: boolean }) {
  const { value } = useSm();
  const play = props.play ?? props.base;
  const changed = value(play)?.number !== value(props.base)?.number;
  return <Value valueKey={play} label={props.label} showLabel={props.showLabel} className={changed ? "play-changed" : undefined} />;
}

/** Says that the wound penalty of the current health level is counted in, and where (GRW S. 172). */
function PlayEffectsNote() {
  const { view, value } = useSm();
  const penalty = value(view.losses.life.penalty)?.number ?? 0;
  if (penalty === 0) return null;
  return (
    <p className="play-note small" role="status">
      <Value valueKey={view.losses.life.penalty} label="Wundabzug" showLabel />
      <span>
        eingerechnet.
        {view.losses.playEffects.map((effect) => (
          <span key={effect.id}>
            {" "}
            {effect.summary} <span className="muted">(GRW S. {effect.source.page})</span>
          </span>
        ))}
      </span>
    </p>
  );
}

export function PlayPanel() {
  const { view, value, language } = useSm();
  const schools = view.skills.filter((s) => s.category === "magie" && ((value(s.points)?.number ?? 0) > 0 || view.spells.some((spell) => spell.school === s.id)));
  const general = view.skills.filter((s) => s.category === "allgemein");
  return (
    <>
      <section className="card play-panel">
        <div className="play-values">
          {["lp", "fo", "vtd", "gw", "kw", "ini", "gsw"].map((id) => {
            const derived = view.derived.find((d) => d.id === id);
            return derived ? <PlayValue key={id} base={derived.value} play={derived.play} label={derived.short} showLabel /> : null;
          })}
          <Value valueKey="splitterpunkte" label="SP" showLabel />
        </div>
        <PlayEffectsNote />
      </section>
      <LossCard track="lp" />
      <LossCard track="fo" />
      <RecoveryActions />
      <section className="card play-weapons">
        <h3>Waffen</h3>
        <List aria-label="Waffen" className="weapon-list">
          {view.weapons.length === 0 && <li className="list-empty">Keine Waffen.</li>}
          {view.weapons.flatMap((w) =>
            w.profiles.map((p, i) => (
              <ListRow
                key={`${w.instance}${i}`}
                className="weapon-row"
                title={
                  <>
                    {w.name}
                    {w.profiles.length > 1 && <span className="muted"> ({p.kind})</span>}
                    {w.container && <span className="badge">im {w.container}</span>}
                  </>
                }
                subtitle={
                  <span className="weapon-details">
                    <span>
                      {[p.skill, p.attributes].join(" · ")}
                      {p.rangeKey && (
                        <>
                          {" · "}
                          <Value valueKey={p.rangeKey} /> m
                        </>
                      )}
                      {p.features.length > 0 && ` · ${p.features.map((f) => f.name).join(", ")}`}
                    </span>
                    {i === 0 && <AmmoCounter weapon={w} />}
                  </span>
                }
                value={
                  <span className="play-stats weapon-stats">
                    <PlayValue base={p.fw} play={p.fwPlay} label="FW" showLabel />
                    <Value valueKey={p.damage} label="Schaden" showLabel />
                    <Value valueKey={p.wgs} label="WGS" showLabel />
                  </span>
                }
              />
            )),
          )}
        </List>
      </section>
      {view.shields.length > 0 && (
        <section className="card">
          <h3>Schilde</h3>
          <List aria-label="Schilde">
            {view.shields.map((s) => (
              <ListRow
                key={s.instance}
                title={
                  <>
                    {s.name}
                    {!s.equipped && <span className="muted"> (nicht angelegt)</span>}
                  </>
                }
                subtitle={[shieldSkills(s), s.features.map((f) => f.name).join(", ")].filter(Boolean).join(" · ")}
                below={
                  <div className="play-stats">
                    {s.defense && <PlayValue base={s.defense} play={s.defensePlay} label="Abwehr" showLabel />}
                    {s.bash && <PlayValue base={s.bash} play={s.bashPlay} label="Schildstoß" showLabel />}
                    {s.bashDamage && <Value valueKey={s.bashDamage} label="Schaden" showLabel />}
                    {s.bashWgs && <Value valueKey={s.bashWgs} label="WGS" showLabel />}
                  </div>
                }
              />
            ))}
          </List>
        </section>
      )}
      {schools.length > 0 && (
        <section className="card stack">
          <h3>Magie</h3>
          {schools.map((school) => {
            const spells = view.spells.filter((spell) => spell.school === school.id);
            return (
              <SchoolBox key={school.id} name={school.name} heading="h4" value={<PlayValue base={school.value} play={school.play} label="FW" showLabel />}>
                <List aria-label={school.name}>
                  {spells.length === 0 && <li className="list-empty">Keine Zauber.</li>}
                  {spells.map((spell) => (
                    <ListRow
                      key={spell.id}
                      title={spell.name}
                      badges={spell.grade != null && <span className="tag">Grad {spell.grade}</span>}
                      subtitle={
                        <span className="play-facts">
                          <Fact label="Schw.">{spell.difficulty}</Fact>
                          <Fact label="Kosten" notes={spell.costNotes}>
                            {spell.cost}
                          </Fact>
                          <Fact label="Zauberdauer">{spell.castingTime}</Fact>
                          <Fact label="Reichw." notes={spell.rangeNotes}>
                            {spell.range}
                          </Fact>
                          <Fact label="Wirkungsdauer">{spell.duration}</Fact>
                          <Fact label="Wirkungsbereich">{spell.area ?? ""}</Fact>
                        </span>
                      }
                      below={
                        (spell.summary || spell.description) && (
                          <>
                            {spell.summary && <div className="small">{spell.summary}</div>}
                            {describe(spell.description, language)}
                          </>
                        )
                      }
                    />
                  ))}
                </List>
              </SchoolBox>
            );
          })}
        </section>
      )}
      <section className="card">
        <h3>Fertigkeiten</h3>
        <ul className="play-skills" aria-label="Fertigkeiten">
          {general.map((skill) => (
            <li key={skill.id}>
              <span>{skill.name}</span>
              <PlayValue base={skill.value} play={skill.play} label={skill.name} />
            </li>
          ))}
        </ul>
      </section>
    </>
  );
}
